import React, { useState, useEffect, useRef } from 'react'
import { withRouter } from "react-router-dom"
import { connect } from "react-redux"
import { compose } from "redux"
import Notification from './components/Notification.jsx'
import { 
  Event, 
  AccessTime, 
  ErrorOutline,
  LocationOn
} from '@material-ui/icons';

import {
  setUserLocation,
  addUserData,
  setOrderScreen,
  setFormattedAddress,
  setNotificationData,
  setUserData
} from '../../redux/actions'

import translation from '../../translations/FirstOrderScreen'
import headerTranslation from '../../translations/OrderBlockHeader'
import Number from '../../components/numpad/Number'
import Api from '../../utils/api/Api'
import { userCreateGuestSession } from '../../utils/helpers'
import { Button } from '../../components/refectored/Button'
import { formatDst } from '../../mixin/date'

const FirstOrderScreen = props => {
  const { notification } = props;
  // const
  return (
    <main className="order-block-body order-block-body-grid">
      {notification.status && <Notification />}
      <CheckLocationInputs {...props} />
    </main>
  )
}

const ShowServicePoint = ({ point }) => {
  return (
    <div className="service-point-card">
      <div className="service-point-icon">
        <LocationOn />
      </div>
      <div className="service-point-content">
        <div className="service-point-company">{ point.company }</div>
        <div className="service-point-address">{ point.addressLine }</div>
      </div>
    </div>
  )
}

const ShowError = ({error}) => {
  const lang = localStorage.getItem('lang')
  
  return (
    <div className="error-container">
      <div className="error-content">
        <div className="error-icon">
          <ErrorOutline />
        </div>
        <div className="error-message">
          <h3 className="error-title">{translation.error[lang] || 'Error'}</h3>
          <p className="error-text">{error}</p>
        </div>
      </div>
    </div>
  )
}

const ShowOrder = ({order}) => {
  const lang = localStorage.getItem('lang')

  return (
    <div className="order-summary-container">
      <div className="order-summary-header">
        <h3 className="order-summary-title">{translation.orderDetails[lang]}</h3>
        <div className="order-status-badge">
          <span className="status-dot"></span>
          {translation.confirmed[lang]}
        </div>
      </div>
      
      <div className="order-details-grid">
        <div className="service-point-info order-pickup-info">
          <div className="pickup-details">
            <div className="pickup-date-section">
              <div className="block-title">
                <Event className="block-icon" />
                { translation.pickupDate[lang] }
              </div>
              <div className="pickup-date">{ formatDst(order.pickupTime, 'dddd DD MMMM') }</div>
            </div>
            
            <div className="pickup-time-section">
              <div className="block-title">
                <AccessTime className="block-icon" />
                {translation.pickupTime[lang]}
              </div>
              <div className="time-range">
                <span className="time-label">{translation.between[lang]}</span>
                <span className="time-value">{`${formatDst(order.frames.pickup.from, 'HH:mm')} - ${formatDst(order.frames.pickup.to, 'HH:mm')}`}</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="contact-info-simple" dangerouslySetInnerHTML={{__html: translation.orderContact[lang]}} />
      </div>
    </div>
  )
}

const CheckLocationInputs = () => {
  const lang = localStorage.getItem('lang')
  const [state, setState] = useState({
    showNumpad: true,
    servicePoint: null,
    code: null,
    error: null,
    variation: 1,
    loading: false,
    order: null,
    submitting: false,
  })

  const debounceTimerRef = useRef(null)
  const requestIdRef = useRef(0)

  const onChange = (value) => {
    updateState({code: value})
  }

  const loadServicePoint = (value, requestId) => {
    updateState({loading: true})

    Api.fetchServicePoint(value).then(({ data }) => {
      // Only update state if this is the latest request
      if (requestId === requestIdRef.current) {
        updateState({
          servicePoint: data,
          showNumpad: false,
          error: null,
          code: value,
          loading: false,
        })
      }
    }).catch(async () => {
      // Only update state if this is the latest request
      if (requestId === requestIdRef.current) {
        updateState({
          servicePoint: null,
          showNumpad: false,
          error: translation.locationNotFound[lang],
          code: value,
          loading: false,
        })
      }
    })
  }

  const clear = (e) => {
    e.preventDefault()

    // Clear any pending debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    updateState({
      showNumpad: true,
      servicePoint: null,
      error: null,
      code: null,
      variation: state.variation + 1,
      loading: false,
      submitting: false,
      order: null,
    })
  }

  const updateState = (newState) => {
    const oldState = {...state}

    setState({...oldState, ...newState})
  }

  useEffect(() => {
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // Only trigger API call if code has at least 4 characters
    if (state.code && state.code.length >= 4) {
      // Increment request ID for the new request
      requestIdRef.current += 1
      const currentRequestId = requestIdRef.current

      // Debounce the API call by 500ms
      debounceTimerRef.current = setTimeout(() => {
        updateState({showNumpad: false})
        loadServicePoint(state.code, currentRequestId)
      }, 500)
    } else {
      // Reset state if code is too short
      updateState({
        servicePoint: null,
        error: null,
        loading: false,
      })
    }

    // Cleanup timer on unmount
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [state.code])

  const placeOrder = async () => {
    updateState({submitting: true})

    await userCreateGuestSession()

    Api.placeNoTerminalOrder(state.code).then(({ data }) => {
      updateState({
        showNumpad: false,
        error: null,
        order: data,
        submitting: false,
      })
    }).catch(async (err) => {
      console.log(err);
      updateState({
        servicePoint: null,
        showNumpad: false,
        error: translation.orderPlacingError[lang],
        submitting: false,
      })
    })
  }

  return (
    <form
      key={state.variation}
      className="first-screen-inputs"
      onSubmit={(e) => {
        e.preventDefault()
      }}
    >
      <div className="card-header">
        <div className="welcome-text">
          <h1>
            {state.order
              ? translation.orderPlaced[lang]
              : headerTranslation.titleNoTerminal[lang]}
          </h1>
        </div>
      </div>

      <div className="card-top-section">
        <div className="input-block">
          <h2>
            {state.order
              ? translation.orderPlaced[lang]
              : translation.fillLocationCode[lang]}
          </h2>
          <div className="input-wrapper">
            <Number
              onChange={onChange}
              label=""
              placeholder={'7430'}
              decimal={false}
              negative={false}
              position="top"
              sync={true}
              inline={true}
              show={state.showNumpad && !state.loading}
              // loading={state.loading}
              leadingZeros={true}
            />
          </div>
        </div>

        <div className="image-block">
          <img
            src={
              require('../../images/figma-design/retail-image-5b5292.png')
                .default
            }
            alt="Dobbi Service"
          />
        </div>
      </div>

      <div
        className="card-bottom-section"
        style={
          state.showNumpad
            ? { paddingBottom: '200px' }
            : { paddingBottom: '50px' }
        }
      >
        <div className="main-block">
          {state.servicePoint && !state.loading && (
            <div className="service-point-wrapper-mobile">
              <ShowServicePoint point={state.servicePoint} />
            </div>
          )}

          {state.error && !state.loading && <ShowError error={state.error} />}

          {state.order && <ShowOrder order={state.order} />}

          {state.code && !state.loading && !state.order && (
            <div className="service-point-info service-point-actions">
              <Button
                color="blue"
                onClick={() => placeOrder()}
                submitting={state.submitting}
                className="checked"
              >
                {translation.placeOrder[lang]}
              </Button>
            </div>
          )}

          {!state.showNumpad && !state.loading && !state.submitting && (
            <div className="clear-link-wrapper">
              <a href="#" className="clear-link" onClick={clear}>
                {state.code && !state.order
                  ? translation.orEnterNewCode[lang]
                  : translation.enterNewCode[lang]}
              </a>
            </div>
          )}
        </div>

        <div className="contact-block">
          {state.servicePoint && !state.loading && (
            <div className="service-point-wrapper-desktop">
              <ShowServicePoint point={state.servicePoint} />
            </div>
          )}

          <p
            className="contact-caption"
            dangerouslySetInnerHTML={{
              __html: translation.bannerText(
                ' of bel ons op <a href="tel:+31850187540">+31 (0)85 018 7540</a>'
              )[lang],
            }}
          />
        </div>
      </div>
    </form>
  )
}

const mapStateToProps = ({ webView, userLocation, userData, notification }) => ({
  wv: webView,
  userLocation,
  userData,
  notification
});

const mapDispatchToProps = dispatch => {
  return {
    setOrderScreen: data => dispatch(setOrderScreen(data)),
    setFormattedAddress: data => dispatch(setFormattedAddress(data)),
    setUserLocation: data => dispatch(setUserLocation(data)),
    addUserData: data => dispatch(addUserData(data)),
    setNotificationData: data => dispatch(setNotificationData(data)),
    setUserData: data => dispatch(setUserData(data)),
  };
};

export default compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(FirstOrderScreen);
